import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import { PromptActions, PromptContainer } from '../../../../v2BaseComponents/Prompt';
import { Loader } from '../../../../v2BaseComponents/Loader';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { getSpacing } from '../../../../../utils/spacing';

const intl = getIntl('EditMedicationGroupPrompt', {
    title: 'Update Other Entries?',
    body: "You've marked this medication {status}. It has {count} other entries in this record. Would you like to mark them all {status}?",
    note: 'Only the active status changes on the other entries.',
    allEntries: 'Mark all {total} {status}',
    thisEntry: 'Just this one',
    cancel: 'Cancel',
    active: 'active',
    inactive: 'inactive',
    updatingOne: 'Updating this entry...',
    updatingMany: 'Updating {count} entries...',
});

export type MedicationGroupScope = 'this-entry' | 'all-entries' | 'cancel';

/**
 * A medication that has been de-duplicated is shown as a single row backed by several entries, and
 * an edit only ever writes to the one being edited. Changing the active status is the case where
 * that surprises people: the row keeps appearing because its other entries are still active. This
 * asks whether to carry the status across the rest of the group.
 *
 * Only the active status propagates - the remaining fields stay on the edited entry, because each
 * entry carries its own dose, dates and prescriber from the system it came from.
 */
const EditMedicationGroupPrompt: React.FC<{
    isOpen: boolean;
    /** Number of other entries grouped with the one being edited. Does not include it. */
    otherEntryCount: number;
    /** The active status the user saved, which decides the wording. */
    isActive: boolean;
    /** Set once a scope is chosen: the prompt stays up and reports progress until the save lands. */
    isSubmitting: boolean;
    /** How many entries the chosen scope is updating, for the progress line. */
    submittingEntryCount: number;
    onSelect: (scope: MedicationGroupScope) => void;
}> = ({ isOpen, otherEntryCount, isActive, isSubmitting, submittingEntryCount, onSelect }) => {
    const statusWord = isActive ? intl('active') : intl('inactive');

    return (
        <PromptContainer
            isOpen={isOpen}
            setIsOpen={(open) => {
                // Dismissing mid-save would hide the only progress the user has.
                if (!open && !isSubmitting) onSelect('cancel');
            }}
            renderPromptContents={() =>
                isSubmitting ? (
                    <Stack
                        vertical
                        fullWidth
                        space={contentStackSpacing}
                        padding={getSpacing(6)}
                        align="center"
                        testID="edit-medication-group-submitting"
                    >
                        <Loader />
                        <Text variant="body-1">
                            {submittingEntryCount === 1
                                ? intl('updatingOne')
                                : intl('updatingMany', { count: submittingEntryCount })}
                        </Text>
                    </Stack>
                ) : (
                    <Stack vertical fullWidth space={contentStackSpacing} padding={getSpacing(6)}>
                        <Text variant="h4">{intl('title')}</Text>
                        <Text variant="body-1">
                            {intl('body', {
                                count: otherEntryCount,
                                status: statusWord,
                            })}
                        </Text>
                        <Text variant="body-2" color="Gray-Text">
                            {intl('note')}
                        </Text>
                        <PromptActions
                            actions={[
                                {
                                    title: intl('allEntries', { total: otherEntryCount + 1, status: statusWord }),
                                    onPress: () => onSelect('all-entries'),
                                    testID: 'edit-medication-group-all',
                                },
                                {
                                    title: intl('thisEntry'),
                                    onPress: () => onSelect('this-entry'),
                                    testID: 'edit-medication-group-this',
                                },
                                {
                                    title: intl('cancel'),
                                    variant: 'secondary',
                                    onPress: () => onSelect('cancel'),
                                    testID: 'edit-medication-group-cancel',
                                },
                            ]}
                        />
                    </Stack>
                )
            }
        />
    );
};

export default React.memo(EditMedicationGroupPrompt);
